import React from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import "./App.css";
import { isMobile } from "react-device-detect";
import {
  UserAuthContextProvider,
  useUserAuth,
} from "./context/userAuthContext";
import { CurrencyProvider } from "./context/CurrencyContext.js";
import { CartProvider } from "./context/CartContext";
import NavigationBar from "./Navbar";
import MobileNavbar from "./MobileNavbar.js";
import AnalyticsTracker from "./AnalyticsTracker.js";
import Notifications from "./Notifications.js";

// Mantener todos los imports de componentes
import Welcome from "./components/Welcome";
import Home from "./components/Home";
import Login from "./components/Login";
import Signup from "./components/Signup";
import PhoneSignUp from "./components/PhoneSignUp";
import ProtectedRoute from "./components/ProtectedRoute";
import NotAuthorizedScreen from "./screens/NotAuthorizedScreen.jsx";
import UserMenu from "./components/Users/UserMenu";
import UserForm from "./components/Users/UserForm.js";
import ControlPanel from "./components/AdminPanel/ControlPanel";
import Profile from "./components/Profile/Profile";
import SearchFriends from "./components/Friends/SearchFriends";
import FriendRequests from "./components/Users/FriendRequests";
import SearchPanel from "./components/Search/SearchPanel";
import CheckOut from "./components/CheckOut/CheckOut.js";
import Success from "./components/CheckOut/Success";
import Failure from "./components/CheckOut/Failure";
import Pending from "./components/CheckOut/Pending";
import CreateStore from "./components/Stores/CreateStore.js";
import AllStores from "./components/Stores/AllStores.js";
import NewUser from "./components/Users/NewUser.js";
import Wallet from "./components/SpeedCoins/Wallet.js";
import TransactionDetails from "./components/SpeedCoins/TransactionDetails.js";
import MyFriends from "./components/Users/MyFriends.js";
import SendNotification from "./components/SendNotification.js";
import ProductDataFetcher from "./components/store/ProductDataFetcher.js";
import Categorias from "./components/store/categorias/Categorias.js";
import ProductosCategoria from "./components/store/categorias/ProductosCategoria.js";
import Search from "./components/store/productos/Search.js";
import Instaladores from "./components/MicroServicios/Instaladores.jsx";
import Servicios from "./components/MicroServicios/Servicios.jsx";
import Cart from "./components/store/carrito/Cart.jsx";
import MembershipView from "./components/Memberships/Membership.jsx";
import NewActivity from "./components/Activities/NewActivity";
import Inbox from "./components/Messenger/Inbox.jsx";
import ActivityInfo from "./components/Activities/ActivityInfo/ActivityInfo.jsx";
import MyActivities from "./components/Activities/MyActivities/MyActivities.jsx";
import NewCategory from "./components/Categories/NewCategory.jsx";
import Feed from "./components/Feed.jsx";
import VineLanding from "./components/LandingPage/VineLanding.jsx";
import RestaurantCategoryUploader from "./components/Restaurants/RestaurantCategoryUploader.jsx";
import RestaurantExplorer from "./components/Restaurants/RestaurantExplorer.jsx";
import RestBack from "./components/Restaurants/RestBack.jsx";
import FoodQuiz from "./components/Restaurants/FoodQuiz/FoodQuiz.jsx";
import NewPlace from "./components/Places/NewPlace.jsx";
import Orders from "./components/Places/Orders.jsx";
import BlogMaker from "./components/Blogs/BlogMaker.jsx";
import BlogList from "./components/Blogs/BlogList.jsx";
import BlogArticleView from "./components/Blogs/BlogArticleView.jsx";
import HouseCounterMap from "./components/Experimentos/Maplytics/HouseCounter.jsx";
import BitcoinWallet from "./components/Btc/BitcoinWallet.jsx";
import EducateBTC from "./components/Btc/EducateBTC.jsx";

// Componente AuthWrapper mejorado
// Componente AuthWrapper mejorado
const AuthWrapper = ({ children }) => {
  const { user, loading } = useUserAuth();
  const location = useLocation();

  // Rutas públicas y sus patrones
  const publicRoutesPatterns = [
    "/",
    "/login",
    "/signup",
    "/phonesignup",
    "/welcomeaboard",
    "/guia",
    "/blog/.*", // Usar un patrón que coincida con /blog/cualquier-slug
    "/maplytics",
    "/btc",
  ];

  // Verificar si la ruta actual coincide con algún patrón de ruta pública
  const isPublicRoute = publicRoutesPatterns.some((pattern) => {
    if (pattern.includes(".*")) {
      // Si el patrón contiene '.*', tratarlo como una expresión regular
      const regexPattern = new RegExp(`^${pattern.replace(".*", ".+")}$`);
      return regexPattern.test(location.pathname);
    }
    // De lo contrario, hacer una comparación exacta
    return pattern === location.pathname;
  });

  // Comprobar si estamos en la página de inicio
  const isHomePage = location.pathname === "/";

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-orange-50 to-white">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando...</p>
        </div>
      </div>
    );
  }

  if (!user && !isPublicRoute) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  // Solo redireccionar las rutas específicamente públicas sin parámetros
  const nonParametricPublicRoutes = [
    "/login",
    "/signup",
    "/phonesignup",
    "/guia",
  ];
  const shouldRedirectLoggedUser =
    user &&
    nonParametricPublicRoutes.includes(location.pathname) &&
    location.pathname !== "/welcomeaboard";

  if (shouldRedirectLoggedUser) {
    return <Navigate to="/home" replace />;
  }

  // Página de inicio sin navbar
  if (isHomePage) {
    return (
      <div className="flex flex-col min-h-screen">
        <main className="flex-1">{children}</main>
      </div>
    );
  }

  // Para todas las demás páginas (públicas o privadas), mostrar navbar
  return (
    <div className="flex flex-col min-h-screen">
      <NavigationBar />
      <main className={`flex-1 ${isMobile ? "pt-16 pb-20" : "pt-16"}`}>
        {children}
      </main>
      <AnalyticsTracker />

      {isMobile && <MobileNavbar />}
    </div>
  );
};

// EnhancedProtectedRoute component
const EnhancedProtectedRoute = ({ children, role }) => {
  const { user, loading } = useUserAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-16 h-16 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  if (role && user.role !== role) {
    return <Navigate to="/notauthorized" replace />;
  }

  return children;
};

function App() {
  return (
    <UserAuthContextProvider>
      <CurrencyProvider>
        <CartProvider>
          <AuthWrapper>
            <Routes>
              {/* Rutas Públicas */}
              <Route path="/" element={<VineLanding />} />
              <Route path="/guia" element={<RestaurantExplorer />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/phonesignup" element={<PhoneSignUp />} />
              <Route path="/welcomeaboard" element={<NewUser />} />
              <Route path="/blog/:slug" element={<BlogArticleView />} />
              <Route path="/maplytics" element={<HouseCounterMap />} />
              <Route path="/btc" element={<EducateBTC />} />

              {/* Rutas Protegidas */}
              <Route
                path="/home"
                element={
                  <EnhancedProtectedRoute>
                    <Home />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/producto/:documentName"
                element={
                  <EnhancedProtectedRoute>
                    <ProductDataFetcher />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/productos/categoria/:id"
                element={
                  <EnhancedProtectedRoute>
                    <ProductosCategoria />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/search/:input"
                element={
                  <EnhancedProtectedRoute>
                    <Search />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/categorias"
                element={
                  <EnhancedProtectedRoute>
                    <Categorias />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/sendNotification"
                element={
                  <EnhancedProtectedRoute>
                    <SendNotification />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/MyFriends/:uid"
                element={
                  <EnhancedProtectedRoute>
                    <MyFriends />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/transaction/:uid"
                element={
                  <EnhancedProtectedRoute>
                    <TransactionDetails />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/wallet"
                element={
                  <EnhancedProtectedRoute>
                    <Wallet />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/notauthorized"
                element={
                  <EnhancedProtectedRoute>
                    <NotAuthorizedScreen />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/search"
                element={
                  <EnhancedProtectedRoute>
                    <SearchPanel />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/friends"
                element={
                  <EnhancedProtectedRoute>
                    <SearchFriends />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/checkout/:uid"
                element={
                  <EnhancedProtectedRoute>
                    <CheckOut />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/geekdashboard"
                element={
                  <EnhancedProtectedRoute role="admin">
                    <ControlPanel />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/myactivities"
                element={
                  <EnhancedProtectedRoute>
                    <MyActivities />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/newactivity"
                element={
                  <EnhancedProtectedRoute>
                    <NewActivity />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/go/:uid"
                element={
                  <EnhancedProtectedRoute>
                    <ActivityInfo />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/memberships"
                element={
                  <EnhancedProtectedRoute>
                    <MembershipView />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/stores"
                element={
                  <EnhancedProtectedRoute>
                    <AllStores />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/createstore"
                element={
                  <EnhancedProtectedRoute>
                    <CreateStore />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/success/:orderId"
                element={
                  <EnhancedProtectedRoute>
                    <Success />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/failure"
                element={
                  <EnhancedProtectedRoute>
                    <Failure />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/pending"
                element={
                  <EnhancedProtectedRoute>
                    <Pending />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/friendsrequests"
                element={
                  <EnhancedProtectedRoute>
                    <FriendRequests />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/user/:uid"
                element={
                  <EnhancedProtectedRoute>
                    <Profile />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/addProfile"
                element={
                  <EnhancedProtectedRoute>
                    <UserForm />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <EnhancedProtectedRoute>
                    <UserMenu />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/instaladores"
                element={
                  <EnhancedProtectedRoute>
                    <Servicios />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/carrito"
                element={
                  <EnhancedProtectedRoute>
                    <Cart />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/messages"
                element={
                  <EnhancedProtectedRoute>
                    <Inbox />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/newcategory"
                element={
                  <EnhancedProtectedRoute>
                    <NewCategory />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/feed"
                element={
                  <EnhancedProtectedRoute>
                    <Feed />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/guia"
                element={
                  <EnhancedProtectedRoute>
                    <RestaurantExplorer />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/restback"
                element={
                  <EnhancedProtectedRoute>
                    <RestBack />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/newplace"
                element={
                  <EnhancedProtectedRoute>
                    <NewPlace />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/orders"
                element={
                  <EnhancedProtectedRoute>
                    <Orders />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/createblog"
                element={
                  <EnhancedProtectedRoute>
                    <BlogMaker />
                  </EnhancedProtectedRoute>
                }
              />
              <Route
                path="/blog"
                element={
                  <EnhancedProtectedRoute>
                    <BlogList />
                  </EnhancedProtectedRoute>
                }
              />

              {/* Ruta para manejar rutas no encontradas */}
              <Route path="*" element={<Navigate to="/home" replace />} />
            </Routes>
          </AuthWrapper>
        </CartProvider>
      </CurrencyProvider>
    </UserAuthContextProvider>
  );
}

export default App;
